import { createForm, useCreateFormStore } from '@components/form';
import { FormWithUnsavedChangesPrompt } from '@components/generic-modals';
import { HideContainer } from '@components/hide-container';
import { IconButton } from '@components/icon-button';
import { IconEdit } from '@components/icons/edit';
import { Sidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';
import { abbreviateDate } from '@utils/date';

import { useUpdateJourneyTravellerAction } from 'api/traveller-actions/hooks';
import { TravellerJourneyActionDetailed } from 'api/types';
import { getActionStatus } from 'ui/authenticated/main/helpers';

import { ACTION_TYPES } from '../action-config';
import {
  ActionProgressBar,
  FormSidebarFooter,
  isActionError,
  SidebarProps,
  TravellerActionSidebar,
  useEditingContext,
} from '../common';
import {
  DurationBreakdown,
  DurationFields,
  DurationRequirement,
  DurationSummary,
  LOOP_MINIMUM_MINUTES,
  getDurationMinutes,
  normalizeDuration,
  toDurationValues,
} from '../duration';

const ACTION_TYPE = ACTION_TYPES.journey_loop;

export const SidebarContent = ({ shortId }: SidebarProps) => (
  <TravellerActionSidebar
    shortId={shortId}
    actionType={ACTION_TYPE}
    Details={DetailsContent}
    Edit={FormContent}
  />
);

type ContentProps = {
  action: TravellerJourneyActionDetailed<typeof ACTION_TYPE>;
};

const DetailsContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const isError = isActionError(action);
  const status = getActionStatus(action);
  const { startEditing } = useEditingContext();
  const data = action.data;

  const progressText =
    status === 'completed'
      ? t('completed')
      : isError
      ? t('error')
      : status === 'in_progress'
      ? t('inProgress')
      : t('notStarted');

  return (
    <Stack space={6}>
      <ActionProgressBar
        action={action}
        progressText={progressText}
        isError={isError}
        isCard={false}
        progressPercentage=" "
      />
      <Sidebar.Separator />
      <HideContainer title={t('configuration')}>
        <Stack space={4}>
          <div>
            <Text font="regular14" text={t('configurationDescription')} />
            <div>
              <DurationBreakdown
                parts={{ days: data.days, hours: data.hours, minutes: data.minutes }}
              />
            </div>
          </div>
          <div>
            <Text font="regular14" text={t('totalRuns')} />
            <Text font="semibold14" text={data.runCounter ?? 0} />
          </div>
          <div>
            <Text
              font="regular14"
              text={action.status === 'completed' ? t('lastRun') : t('nextScheduledRun')}
            />
            {data.date ? (
              <Text font="semibold14" text={abbreviateDate(data.date, 'en', 'datetime')} />
            ) : (
              <Text font="semibold14" text={t('afterSuccess')} />
            )}
          </div>
        </Stack>
      </HideContainer>
      <Sidebar.Separator />
      {status !== 'completed' && (
        <Sidebar.Footer>
          <IconButton variant="ghost-purple" icon={<IconEdit />} onClick={startEditing} />
        </Sidebar.Footer>
      )}
    </Stack>
  );
};

type Data = {
  days: string;
  hours: string;
  minutes: string;
};

const Form = createForm<Data>();

const FormContent = ({ action }: ContentProps) => {
  const { t } = useScopedTranslate(sidebarLocale);
  const { stopEditing } = useEditingContext();
  const formStore = useCreateFormStore();
  const days = formStore.useFieldValue<string>('days') ?? '';
  const hours = formStore.useFieldValue<string>('hours') ?? '';
  const minutes = formStore.useFieldValue<string>('minutes') ?? '';
  const defaults = normalizeDuration({
    days: action.values?.days?.value,
    hours: action.values?.hours?.value ?? '12',
    minutes: action.values?.minutes?.value,
  });
  const durationMinutes = getDurationMinutes({ days, hours, minutes });
  const isValid = durationMinutes >= LOOP_MINIMUM_MINUTES;
  const isDurationDirty = durationMinutes !== getDurationMinutes(defaults);

  const isDirty = formStore.useIsDirty();
  const editMutation = useUpdateJourneyTravellerAction(action, {
    onSuccess: stopEditing,
  });

  const handleSave = (data: Data) => {
    const values = toDurationValues(data, LOOP_MINIMUM_MINUTES);
    if (!values) return false;
    return editMutation.mutateAsync({
      values,
    });
  };

  return (
    <FormWithUnsavedChangesPrompt store={formStore} onSubmit={(data) => handleSave(data)}>
      <Stack space={6}>
        <HideContainer title={t('actionDetails')}>
          <Text font="regular14" text={t('loopDescription')} />
        </HideContainer>
        <Sidebar.Separator />
        <HideContainer title={t('loopTimer')}>
          <Stack space={4}>
            <DurationFields form={Form} defaults={defaults} />
            <DurationSummary variant="loop" parts={{ days, hours, minutes }} />
            {!isValid && <DurationRequirement variant="loop" isDirty={isDurationDirty} />}
          </Stack>
        </HideContainer>
        <FormSidebarFooter
          Form={Form}
          isDirty={isDirty}
          editMutation={editMutation}
          hasNotifyError={!isValid}
        />
      </Stack>
    </FormWithUnsavedChangesPrompt>
  );
};

const sidebarLocale = {
  en: {
    configuration: 'Configuration',
    completed: 'Completed',
    inProgress: 'In progress',
    error: 'Error',
    notStarted: 'Not started',
    dataNotFound: 'Data not found',
    configurationDescription: 'The loop is configured to restart every',
    totalRuns: 'Total runs:',
    lastRun: 'Last run:',
    nextScheduledRun: 'Next scheduled run:',
    selectHowLong:
      'Select for how long journey step should wait before triggreing the following action',
    useWhen: 'Use when you want to pause by a defined amount of time between actions in a journey',
    daysPassed: '{days} Days',
    triggersNextAction: 'Triggers next action',
    actionDetails: 'Action details',
    loopTimer: 'Loop timer',
    loopDescription: 'Use when you want to repeat some actions until a condition is met.',
    waitsFor: 'Waits for',
    day: 'Day',
    hour: 'Hour',
    minute: 'Minute',
    afterSuccess: 'When preceding action completes',
  },
};
